import { useResponsive } from '@teachino/shared-design-system';
import type { EditorSaveState } from '@teachino/shared-editor';
import { $t } from '@teachino/shared-i18n';
import {
  type InfrastructureCommand,
  InfrastructureCommandBuilder,
  type InfrastructureResponse,
  InfrastructureResponseBuilder,
} from '@teachino/shared-infrastructure';
import type { Course } from '@teachino/shared-legacy-api';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { CombinedError, useMutation, useQuery } from '@urql/vue';
import { computed, type ComputedRef, ref, type Ref } from 'vue';
import * as z from 'zod';
import {
  convertTopicListItemFragment,
  type TopicListItem,
} from '../folders/topic-list-item';
import { createMaterialMutation } from './create-material.mutation';
import { deleteMaterialMutation } from './delete-material.mutation';
import { filterTopicsQuery } from './filter-topics.query';
import {
  generateLinkPreviewMutation,
  type LinkPreview,
} from './generate-link-preview.mutation';
import {
  convertMaterialFragment,
  type MaterialDetails,
} from './material-details.fragment';
import { materialAnnotationToolAccessQuery } from './material-annotation-tool-access.query';
import { materialDetailsQuery } from './material-details.query';
import type { MaterialsFacade } from './materials.facade';
import { updateMaterialMutation } from './update-material.mutation';
import { migrateMaterialMutation } from './migrate-material.mutation';

const MIN_MATERIAL_NAME_LENGTH_FOR_AI = 3;
const MILLISECONDS_PER_SECOND = 1000;
const TOKEN_REFRESH_MARGIN_SECONDS = 10;
const TOKEN_REFRESH_MARGIN_MS =
  TOKEN_REFRESH_MARGIN_SECONDS * MILLISECONDS_PER_SECOND;

export class MaterialsService implements MaterialsFacade {
  validators = {
    name: z.string().min(1, $t('generic_form_error_required')),
  };
  private globalDi = useGlobalDependencyProvider();
  private analytics = this.globalDi.inject('analytics');
  private user = this.globalDi.inject('user');
  private _createMaterial = useMutation(createMaterialMutation);
  private _updateMaterial = useMutation(updateMaterialMutation);
  isSaving: ComputedRef<EditorSaveState> = computed(() =>
    this._updateMaterial.fetching.value ? 'saving' : 'saved',
  );
  private _migrateMaterial = useMutation(migrateMaterialMutation);
  private selectedMaterial = ref<string | null>(null);
  // Chat visibility state
  private _chatVisible = ref(useResponsive().isExtraLarge.value);
  chatVisible = computed(
    () =>
      this._chatVisible.value && this.user.hasPermission('ai_advanced').value,
  );
  private _deleteMaterial = useMutation(deleteMaterialMutation);

  private _generateLinkPreview = useMutation(generateLinkPreviewMutation);

  private _materialDetails = useQuery({
    query: materialDetailsQuery,
    variables: computed(() => {
      return {
        id: this.selectedMaterial.value ?? '',
      };
    }),
    pause: computed(() => this.selectedMaterial.value === null),
  });

  private _annotationToolAccessToken = ref<string | null>(null);
  private _annotationToolAccessExpiresAtMs = ref<number | null>(null);
  private _annotationToolAccessQuery = useQuery({
    query: materialAnnotationToolAccessQuery,
    variables: computed(() => {
      return {
        id: this.selectedMaterial.value ?? '',
      };
    }),
    pause: true,
    requestPolicy: 'network-only',
  });

  toggleChatVisibility(visibility?: boolean) {
    this._chatVisible.value = visibility ?? !this._chatVisible.value;
  }

  showChat(): void {
    this._chatVisible.value = true;
  }

  createNewMaterial(args: {
    topicId: string;
    name?: string;
    contentHtml?: string;
    initialIpiRequestId?: string | null;
  }): InfrastructureCommand<{ id: string }> {
    const newEditor = this.user.materialEditorMode.value === 'new';
    const defaultName = $t('material.default_name');
    const { topicId, name, contentHtml, initialIpiRequestId } = args;
    return InfrastructureCommandBuilder.fromMutation(this._createMaterial, {
      icon: ':memo:',
      topicId,
      contentHtml,
      name: name ?? defaultName,
      initialIpiRequestId,
      newEditor,
    })
      .withMapper((result) => {
        return { id: result.createMaterial.material.id };
      })
      .build();
  }

  selectMaterial(id: string): void {
    this.selectedMaterial.value = id;
    this.reloadMaterialDetails();
    this.ensureAnnotationToolAccessLoaded();
  }

  materialDetails(): InfrastructureResponse<MaterialDetails> {
    return InfrastructureResponseBuilder.fromQuery(this._materialDetails)
      .withMapper((result) => convertMaterialFragment(result.material))
      .build();
  }

  annotationToolAccess() {
    return {
      token: computed(() => {
        if (!this.hasValidAnnotationToolAccess()) {
          return null;
        }

        return this._annotationToolAccessToken.value;
      }),
      ensureLoaded: () => {
        this.ensureAnnotationToolAccessLoaded();
      },
    };
  }

  courseMaterialDetails(): InfrastructureResponse<MaterialDetails> {
    return InfrastructureResponseBuilder.fromQuery(this._materialDetails)
      .withMapper((result) => convertMaterialFragment(result.material))
      .build();
  }

  fileNameForOriginalName(fileName: string): string {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex <= 0) {
      return fileName;
    }
    return fileName.substring(0, lastDotIndex);
  }

  async createMaterialForFiles(args: {
    topicId: string;
    files: { originalName: string; url: string; key: string }[];
  }): Promise<void> {
    let fileCount = 0;
    let imageCount = 0;
    for (const file of args.files) {
      let contentHtml;

      //TODO: for Boogie from Stefan: use di config
      /*const config = FileTool.di.inject('config');
      const isImage = config
        .uploadConfig()
        .imageUpload.allowedMimeTypes.includes(file.type);*/
      const extension = file.originalName.split('.').pop()?.toLowerCase() ?? '';
      const isImage = ['jpg', 'jpeg', 'png'].includes(extension);
      if (isImage) {
        imageCount++;
        contentHtml = `<teachino-image key="${file.key}" originalname="${file.originalName}" url="${file.url}"></teachino-image><p></p>`;
      } else {
        fileCount++;
        contentHtml = `<teachino-file key="${file.key}" originalname="${file.originalName}" url="${file.url}"></teachino-file><p></p>`;
      }

      const newEditor = this.user.materialEditorMode.value === 'new';

      await InfrastructureCommandBuilder.fromMutation(this._createMaterial, {
        topicId: args.topicId,
        contentHtml,
        name: $t('material.default_name'),
        newEditor,
      })
        .build()
        .run();
    }
    this.analytics.track('materials-bulk-imported', { imageCount, fileCount });
  }

  updateMaterial(args: {
    id: string;
    html: string;
  }): InfrastructureCommand<void> {
    const { id, html } = args;

    return InfrastructureCommandBuilder.fromMutation(this._updateMaterial, {
      id,
      contentHtml: { html },
    })
      .withSideeffect(() => this.reloadMaterialDetails())
      .build();
  }

  migrateMaterial(args: {
    id: string;
    html: string;
  }): InfrastructureCommand<void> {
    const { id, html } = args;

    return InfrastructureCommandBuilder.fromMutation(this._migrateMaterial, {
      id,
      contentHtml: { html },
    })
      .withSideeffect(() => this.reloadMaterialDetails())
      .build();
  }

  updateName(args: { id: string; name: string }): InfrastructureCommand<void> {
    const { id, name } = args;
    return InfrastructureCommandBuilder.fromMutation(this._updateMaterial, {
      id,
      name,
    })
      .withSideeffect(() => this.reloadMaterialDetails())
      .build();
  }

  updateDuration(args: {
    id: string;
    duration: number | null;
  }): InfrastructureCommand<void> {
    const { id, duration } = args;
    return InfrastructureCommandBuilder.fromMutation(this._updateMaterial, {
      id,
      duration,
    })
      .withSideeffect(() => this.reloadMaterialDetails())
      .build();
  }

  updateIcon(args: {
    id: string;
    icon: string | null;
  }): InfrastructureCommand<void> {
    const { id, icon } = args;
    return InfrastructureCommandBuilder.fromMutation(this._updateMaterial, {
      id,
      icon,
    })
      .withSideeffect(() => this.reloadMaterialDetails())
      .build();
  }

  deleteMaterial(args: {
    materialId: string;
    topicId: string;
  }): InfrastructureCommand<void, { affectedCourses: Course[] }> {
    return InfrastructureCommandBuilder.fromMutation(this._deleteMaterial, {
      id: args.materialId,
      topicId: args.topicId,
      force: false,
    })
      .withErrorMapper((error) => {
        return this.parseDeleteMaterialError(error);
      })
      .build();
  }

  confirmMaterialDeletion(args: {
    materialId: string;
    topicId: string;
  }): InfrastructureCommand<void> {
    return InfrastructureCommandBuilder.fromMutation(this._deleteMaterial, {
      id: args.materialId,
      topicId: args.topicId,
      force: true,
    }).build();
  }

  filterTopics(args: {
    name: Ref<string | null>;
    courseId?: string;
  }): InfrastructureResponse<{
    topics: TopicListItem[];
    topicsInCourse: TopicListItem[];
    totalTopicCount: number;
  }> {
    const { name, courseId } = args;
    const query = useQuery({
      query: filterTopicsQuery,
      variables: computed(() => {
        return {
          topicFilterInput: {
            name:
              !name.value || name.value.length === 0 ? undefined : name.value,
          },
        };
      }),
    });
    return InfrastructureResponseBuilder.fromQuery(query)
      .withMapper((res) => {
        const topics = res.topics.map((topic) => {
          return convertTopicListItemFragment(topic);
        });
        const topicsInCourse = courseId
          ? topics.filter((topic) =>
              topic.courses.some((c) => c.id === courseId),
            )
          : [];
        return {
          topics,
          topicsInCourse,
          totalTopicCount: res.libraryMeta.totalTopicsCount,
        };
      })
      .build();
  }

  generateLinkPreview(args: {
    url: string;
  }): InfrastructureCommand<LinkPreview> {
    const { url } = args;
    const materialId = this.materialDetails().data.value?.id;
    if (!materialId) {
      throw new Error('Cannot create preview without material');
    }
    return InfrastructureCommandBuilder.fromMutation(
      this._generateLinkPreview,
      {
        url,
        materialId,
      },
    )
      .withMapper((result) => {
        return result.generateLinkPreview;
      })
      .build();
  }

  isDefaultMaterialName(args: { materialName: string }) {
    return args.materialName === $t('material.default_name');
  }

  isMaterialNameTooShortForAI(args: { materialName: string }) {
    return args.materialName.length <= MIN_MATERIAL_NAME_LENGTH_FOR_AI;
  }

  private reloadMaterialDetails() {
    if (!this.selectedMaterial.value) {
      return;
    }
    this._materialDetails.executeQuery();
  }

  private ensureAnnotationToolAccessLoaded(): void {
    if (!this.selectedMaterial.value) {
      return;
    }

    if (this._annotationToolAccessQuery.fetching.value) {
      return;
    }

    if (this.hasValidAnnotationToolAccess()) {
      return;
    }

    void this.loadAnnotationToolAccess();
  }

  private async loadAnnotationToolAccess(): Promise<void> {
    const selectedMaterialId = this.selectedMaterial.value;

    if (!selectedMaterialId) {
      return;
    }

    const result = await this._annotationToolAccessQuery.executeQuery({
      requestPolicy: 'network-only',
    });

    if (this.selectedMaterial.value !== selectedMaterialId) {
      return;
    }

    const access = result.data.value?.materialAnnotationToolAccess;

    this._annotationToolAccessToken.value = access?.token ?? null;
    this._annotationToolAccessExpiresAtMs.value = access?.expiresAt
      ? new Date(access.expiresAt).getTime()
      : null;
  }

  private hasValidAnnotationToolAccess(): boolean {
    if (!this._annotationToolAccessToken.value) {
      return false;
    }

    if (!this._annotationToolAccessExpiresAtMs.value) {
      return false;
    }

    return (
      Date.now() + TOKEN_REFRESH_MARGIN_MS <
      this._annotationToolAccessExpiresAtMs.value
    );
  }

  private parseDeleteMaterialError(error: CombinedError): {
    affectedCourses: Course[];
  } {
    if (error.graphQLErrors.length === 0) {
      throw new Error('Graphql Error thrown with 0-length Error array');
    }
    const [firstError] = error.graphQLErrors;
    const extensions = firstError.extensions;
    const extensionSchema = z.object({
      myExtensions: z.object({
        data: z.array(
          z.object({
            id: z.string().min(1),
            shortName: z.string().nullable(),
            name: z.string().min(1),
            foregroundColor: z.string().min(1),
            backgroundColor: z.string().min(1),
            __typename: z.literal('Course'),
          }),
        ),
      }),
    });
    const parsedExtensions = extensionSchema.safeParse(extensions);
    if (parsedExtensions.success) {
      const courses = parsedExtensions.data.myExtensions.data;
      const courseMap = new Map<string, Course>();
      courses.forEach((course) => courseMap.set(course.id, course));
      return { affectedCourses: [...courseMap.values()] };
    } else {
      console.error(parsedExtensions.error);
      return { affectedCourses: [] };
    }
  }
}
